const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

async function request(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const headers = { ...options.headers };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const isAuthEndpoint = endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/signup');

  if ((res.status === 401 || res.status === 403) && isAuthEndpoint) {
    const data = await res.json();
    const err = new Error(data.error || 'Request failed');
    if (data.emailNotVerified) err.emailNotVerified = true;
    throw err;
  }

  if (res.status === 401 && !isAuthEndpoint) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export const api = {
  // Auth
  signup: (body) => request('/auth/signup', { method: 'POST', body: JSON.stringify(body) }),
  login: (body) => request('/auth/login', { method: 'POST', body: JSON.stringify(body) }),
  getMe: () => request('/auth/me'),
  // Email verification
  verifyEmail: (token) => request(`/auth/verify-email?token=${token}`),
  resendVerification: () => request('/auth/resend-verification', { method: 'POST' }),

  // Password reset
  forgotPassword: (email) =>
    request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token, password) =>
    request('/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),

  // Business
  createBusiness: (body) => request('/business', { method: 'POST', body: JSON.stringify(body) }),
  getBusiness: () => request('/business'),
  updateBusiness: (body) => request('/business', { method: 'PUT', body: JSON.stringify(body) }),

  // Customers
  getCustomers: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.limit) qs.set('limit', params.limit);
    if (params.search) qs.set('search', params.search);
    const query = qs.toString();
    return request(`/customers${query ? `?${query}` : ''}`);
  },
  addCustomer: (body) => request('/customers', { method: 'POST', body: JSON.stringify(body) }),
  deleteCustomer: (id) => request(`/customers/${id}`, { method: 'DELETE' }),
  updateCustomer: (id, body) => request(`/customers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  uploadCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request('/customers/upload', { method: 'POST', body: formData });
  },

  // Templates
  getTemplates: () => request('/templates'),
  getTemplatePageData: () => request('/templates/page-data'),
  updateTemplate: (id, body) =>
    request(`/templates/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  sendCustomTemplate: (body) =>
    request('/templates/send-custom', { method: 'POST', body: JSON.stringify(body) }),
  getSavedTemplates: () => request('/templates/custom'),
  saveCustomTemplate: (body) =>
    request('/templates/custom', { method: 'POST', body: JSON.stringify(body) }),
  deleteCustomTemplate: (id) =>
    request(`/templates/custom/${id}`, { method: 'DELETE' }),

  // Automation Config
  getAutomationConfig: () => request('/business/automation'),
  updateAutomationConfig: (body) =>
    request('/business/automation', { method: 'PUT', body: JSON.stringify(body) }),

  // Dashboard
  getDashboard: (range, includeActivity = true) => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    if (!includeActivity) params.set('includeActivity', 'false');
    const qs = params.toString();
    return request(`/dashboard${qs ? `?${qs}` : ''}`);
  },
  getDashboardActivity: (skip = 0, limit = 10) => {
    const params = new URLSearchParams();
    if (skip) params.set('skip', skip);
    params.set('limit', limit);
    return request(`/dashboard/activity?${params.toString()}`);
  },
  getActivityDetail: (id) => request(`/dashboard/activity/${id}`),
  getQueuedMessages: (range) => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    const qs = params.toString();
    return request(`/dashboard/queued${qs ? `?${qs}` : ''}`);
  },
  getLinkClicks: (range) => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    const qs = params.toString();
    return request(`/dashboard/link-clicks${qs ? `?${qs}` : ''}`);
  },
  getSentMessages: (range, page = 1, limit = 50) => {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    params.set('page', page);
    params.set('limit', limit);
    const qs = params.toString();
    return request(`/dashboard/sent-messages${qs ? `?${qs}` : ''}`);
  },

  // AI
  improveMessage: (text, category, channel) =>
    request('/ai/improve', { method: 'POST', body: JSON.stringify({ text, category, channel }) }),

  // Password verification
  verifyPassword: (password) =>
    request('/auth/verify-password', { method: 'POST', body: JSON.stringify({ password }) }),

  // Razorpay
  getPublicPlans: (country) => request(`/razorpay/public-plans${country ? `?country=${encodeURIComponent(country)}` : ''}`),
  getPlans: () => request('/razorpay/plans'),
  getSubscriptionPageData: () => request('/razorpay/page-data'),
  createSubscription: (plan) =>
    request('/razorpay/create-subscription', { method: 'POST', body: JSON.stringify({ plan }) }),
  verifyPayment: (body) =>
    request('/razorpay/verify-payment', { method: 'POST', body: JSON.stringify(body) }),
  requestUpgrade: (plan) =>
    request('/razorpay/upgrade', { method: 'POST', body: JSON.stringify({ plan }) }),
  getUpgradePreview: (plan) =>
    request(`/razorpay/upgrade-preview?plan=${plan}`),
  requestDowngrade: (plan) =>
    request('/razorpay/downgrade', { method: 'POST', body: JSON.stringify({ plan }) }),
  cancelDowngrade: () =>
    request('/razorpay/cancel-downgrade', { method: 'POST' }),
  cancelSubscription: (password) =>
    request('/razorpay/cancel', { method: 'POST', body: JSON.stringify({ password }) }),
  cancelCancellation: () =>
    request('/razorpay/cancel-cancellation', { method: 'POST' }),
  getSubscriptionStatus: () =>
    request('/razorpay/status'),
  getBillingMonths: () =>
    request('/razorpay/billing-months'),
  getInvoices: (page = 1, limit = 20) =>
    request(`/razorpay/invoices?page=${page}&limit=${limit}`),
  getInvoice: (id) => request(`/razorpay/invoices/${encodeURIComponent(id)}`),
  downloadInvoicePdf: async (id) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE}/razorpay/invoices/${encodeURIComponent(id)}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Failed to download invoice');
    return res.blob();
  },
  getBill: (period) =>
    request(`/razorpay/bill${period ? `?period=${period}` : ''}`),
  downloadBillPdf: async (period) => {
    const token = localStorage.getItem('token');
    const url = `${API_BASE}/razorpay/bill/pdf${period ? `?period=${period}` : ''}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to download bill');
    }
    return res.blob();
  },

  // WhatsApp
  connectWhatsApp: (body) =>
    request('/whatsapp/connect', { method: 'POST', body: JSON.stringify(body) }),
  getWhatsAppStatus: () => request('/whatsapp/status'),
  disconnectWhatsApp: () => request('/whatsapp/disconnect', { method: 'POST' }),

  // Demo Booking
  submitDemoRequest: (body) =>
    request('/demo', { method: 'POST', body: JSON.stringify(body) }),
  getCalendlyUrl: () => request('/demo/calendly-url'),

  // Support
  createSupportTicket: (body) =>
    request('/support', { method: 'POST', body: JSON.stringify(body) }),
  getMyTickets: () => request('/support/my-tickets'),
  getTicket: (id) => request(`/support/tickets/${id}`),
  replyToTicket: (id, message) =>
    request(`/support/tickets/${id}/reply`, { method: 'POST', body: JSON.stringify({ message }) }),
};

// Admin API (uses separate token storage)
function adminRequest(endpoint, options = {}) {
  const token = localStorage.getItem('adminToken');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return fetch(`${API_BASE}${endpoint}`, { ...options, headers })
    .then(async (res) => {
      if (res.status === 401) {
        localStorage.removeItem('adminToken');
        if (!endpoint.includes('/admin/login')) {
          window.location.href = '/admin-login';
        }
        const data = await res.json();
        throw new Error(data.error || 'Admin session expired');
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      return data;
    });
}

export const adminApi = {
  login: (body) => adminRequest('/admin/login', { method: 'POST', body: JSON.stringify(body) }),
  getMe: () => adminRequest('/admin/me'),
  getOverview: () => adminRequest('/admin/overview'),
  getTickets: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.status) qs.set('status', params.status);
    if (params.priority) qs.set('priority', params.priority);
    if (params.search) qs.set('search', params.search);
    return adminRequest(`/admin/tickets?${qs.toString()}`);
  },
  getTicket: (id) => adminRequest(`/admin/tickets/${id}`),
  replyToTicket: (id, message) =>
    adminRequest(`/admin/tickets/${id}/reply`, { method: 'POST', body: JSON.stringify({ message }) }),
  updateTicket: (id, body) =>
    adminRequest(`/admin/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  getDemos: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.search) qs.set('search', params.search);
    if (params.bookingStatus) qs.set('bookingStatus', params.bookingStatus);
    if (params.sort) qs.set('sort', params.sort);
    if (params.order) qs.set('order', params.order);
    return adminRequest(`/admin/demos?${qs.toString()}`);
  },
  updateDemo: (id, body) =>
    adminRequest(`/admin/demos/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  getUsers: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.search) qs.set('search', params.search);
    if (params.plan) qs.set('plan', params.plan);
    return adminRequest(`/admin/users?${qs.toString()}`);
  },
  getUsageAnalytics: () => adminRequest('/admin/usage-analytics'),
  updateUserPlan: (userId, plan) =>
    adminRequest(`/admin/users/${userId}/plan`, { method: 'PATCH', body: JSON.stringify({ plan }) }),

  // System Alerts
  getAlerts: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.status) qs.set('status', params.status);
    if (params.severity) qs.set('severity', params.severity);
    if (params.type) qs.set('type', params.type);
    return adminRequest(`/admin/alerts?${qs.toString()}`);
  },
  getAlertSummary: () => adminRequest('/admin/alerts/summary'),
  acknowledgeAlert: (id) =>
    adminRequest(`/admin/alerts/${id}/acknowledge`, { method: 'PATCH' }),
  resolveAlert: (id) =>
    adminRequest(`/admin/alerts/${id}/resolve`, { method: 'PATCH' }),
  bulkResolveAlerts: (ids) =>
    adminRequest('/admin/alerts/bulk-resolve', { method: 'POST', body: JSON.stringify({ ids }) }),
  resolveAllAlerts: (severity) =>
    adminRequest('/admin/alerts/resolve-all', { method: 'POST', body: JSON.stringify({ severity }) }),
};
